// ── Anti-lag rule tables ────────────────────────────────────────────────────
// Shared between the network-wide configuration panel (every target) and a
// server's "Effective Anti-Lag Config" card (only the rules in force there),
// so both stay in step when the config document grows another rule kind.

import type {
  ChunkThrottleConfig,
  MobAiThrottleConfig,
  MsptThrottleConfig,
  PlayerCountThrottleConfig,
  ServerWideThrottle,
} from '../../types/throttle';
import {
  describeThrottleMulti,
  describeThrottleTarget,
  formatTicksApprox,
  isThrottleInForce,
  isUnknownThrottleConfig,
} from '../../utils/throttle';
import { cn } from '../../utils/cn';

// Dense variants of the shared table cells (tables sit inside cards / subcards).
const HEAD_CELL = 'th px-3';
const CELL = 'td px-3 py-2 text-glass-300';
const NUM_CELL = 'td px-3 py-2 text-glass-300 tabular-nums';
const ID_CELL = 'td px-3 py-2 text-white font-medium font-mono text-xs';
const ROW = '';

/** Placeholder for a value the API did not send. */
const Dash = () => <span className="text-glass-500">—</span>;

const TargetHead = () => <th className={HEAD_CELL}>Applies to</th>;

const TargetCell = ({ target }: { target: string }) => (
  <td className={CELL} title={target}>
    {describeThrottleTarget(target)}
  </td>
);

const ThrottleCell = ({ multi }: { multi: number | null | undefined }) => (
  <td className={NUM_CELL}>
    {typeof multi === 'number' ? (
      <>
        {describeThrottleMulti(multi)} <span className="text-glass-500">(×{multi})</span>
      </>
    ) : (
      <Dash />
    )}
  </td>
);

interface RuleTableProps {
  /** Render the "Applies to" column — the network-wide panel lists every target. */
  showTarget?: boolean;
}

interface MsptRuleTableProps extends RuleTableProps {
  rules: MsptThrottleConfig[];
}

/** Per-chunk MSPT tiers: a chunk over the threshold gets warned, then throttled. */
export const MsptRuleTable = ({ rules, showTarget = false }: MsptRuleTableProps) => (
  <div className="overflow-x-auto">
    <table className="table">
      <thead>
        <tr>
          <th className={HEAD_CELL}>Rule</th>
          {showTarget && <TargetHead />}
          <th
            className={HEAD_CELL}
            title="Whether the rule targets all chunks or only force-loaded ones"
          >
            Chunks
          </th>
          <th
            className={HEAD_CELL}
            title="Absolute floor in ms/tick a chunk must exceed for this tier to apply"
          >
            Threshold
          </th>
          <th className={HEAD_CELL} title="Percentage component of the flagging trigger">
            Threshold %
          </th>
          <th
            className={HEAD_CELL}
            title="Tick-rate multiplier applied once a chunk is THROTTLED — ×1 leaves it at full speed (warning only)"
          >
            Throttle
          </th>
          <th className={HEAD_CELL} title="WARNING → THROTTLED grace period in game ticks">
            Grace period
          </th>
        </tr>
      </thead>
      <tbody>
        {rules.map((rule) => (
          <tr key={`${rule.target}-${rule.id}`} className={ROW}>
            <td className={ID_CELL}>{rule.id}</td>
            {showTarget && <TargetCell target={rule.target} />}
            <td className={CELL}>{rule.foreloadedOnly ? 'Force-loaded only' : 'All chunks'}</td>
            <td className={NUM_CELL}>
              {typeof rule.thresholdMSPT === 'number' && rule.thresholdMSPT > 0 ? (
                `${rule.thresholdMSPT} ms/tick`
              ) : (
                <Dash />
              )}
            </td>
            <td className={NUM_CELL}>
              {typeof rule.thresholdPercentage === 'number' ? (
                `${rule.thresholdPercentage}%`
              ) : (
                <Dash />
              )}
            </td>
            <ThrottleCell multi={rule.throttleMulti} />
            <td className={NUM_CELL}>
              {typeof rule.throttleDelayTicks === 'number' ? (
                <>
                  {rule.throttleDelayTicks.toLocaleString()} ticks{' '}
                  <span className="text-glass-500">
                    ({formatTicksApprox(rule.throttleDelayTicks)})
                  </span>
                </>
              ) : (
                <Dash />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

interface PlayerCountRuleTableProps extends RuleTableProps {
  rules: PlayerCountThrottleConfig[];
  /** Tier the API reports as in force right now, if the caller knows it. */
  activeThrottle?: ServerWideThrottle | null;
}

/**
 * Server-wide population throttles: once a server holds `minPlayerCount`
 * players everything ticks slower, regardless of which chunks are busy.
 */
export const PlayerCountRuleTable = ({
  rules,
  showTarget = false,
  activeThrottle = null,
}: PlayerCountRuleTableProps) => (
  <div className="overflow-x-auto">
    <table className="table">
      <thead>
        <tr>
          <th className={HEAD_CELL}>Rule</th>
          {showTarget && <TargetHead />}
          <th className={HEAD_CELL} title="Player count at which this tier starts applying">
            Players
          </th>
          <th
            className={HEAD_CELL}
            title="Server-wide tick-rate multiplier while the tier is in force"
          >
            Throttle
          </th>
        </tr>
      </thead>
      <tbody>
        {rules.map((rule) => {
          const inForce = isThrottleInForce(rule, activeThrottle);
          return (
            <tr key={`${rule.target}-${rule.id}`} className={cn(ROW, inForce && 'bg-amber-500/10')}>
              <td className={ID_CELL}>
                {rule.id}
                {inForce && (
                  <span
                    className="ml-2 badge badge-amber font-sans cursor-default"
                    title="The tier the API reports as currently in force on this server"
                  >
                    In force
                  </span>
                )}
              </td>
              {showTarget && <TargetCell target={rule.target} />}
              <td className={NUM_CELL}>
                {typeof rule.minPlayerCount === 'number' ? `${rule.minPlayerCount}+` : <Dash />}
              </td>
              <ThrottleCell multi={rule.throttleMulti} />
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

interface MobAiRuleTableProps extends RuleTableProps {
  rules: MobAiThrottleConfig[];
}

/** Population-based mob-AI throttles (a separate list in the config document). */
export const MobAiRuleTable = ({ rules, showTarget = false }: MobAiRuleTableProps) => (
  <div className="overflow-x-auto">
    <table className="table">
      <thead>
        <tr>
          <th className={HEAD_CELL}>Rule</th>
          {showTarget && <TargetHead />}
          <th className={HEAD_CELL} title="Player count at which this tier starts applying">
            Players
          </th>
          <th
            className={HEAD_CELL}
            title="Mobs further than this from every player stop ticking their AI"
          >
            AI radius
          </th>
        </tr>
      </thead>
      <tbody>
        {rules.map((rule) => (
          <tr key={`${rule.target}-${rule.id}`} className={ROW}>
            <td className={ID_CELL}>{rule.id}</td>
            {showTarget && <TargetCell target={rule.target} />}
            <td className={NUM_CELL}>
              {typeof rule.minPlayerCount === 'number' ? `${rule.minPlayerCount}+` : <Dash />}
            </td>
            <td className={NUM_CELL}>
              {typeof rule.playerDistanceChunks === 'number' ? (
                `${rule.playerDistanceChunks} chunk${rule.playerDistanceChunks === 1 ? '' : 's'}`
              ) : (
                <Dash />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Says so when the config document carries rule types this build cannot
 * render — a silently short table is worse than an explicit gap.
 */
export const UnknownRulesNote = ({ rules }: { rules: ChunkThrottleConfig[] }) => {
  const unknown = rules.filter(isUnknownThrottleConfig);
  if (unknown.length === 0) return null;
  const types = Array.from(new Set(unknown.map((rule) => rule.type ?? 'untyped'))).join(', ');
  return (
    <p className="mt-2 text-xs text-yellow-300">
      {unknown.length} rule{unknown.length === 1 ? '' : 's'} of an unrecognised type ({types}){' '}
      {unknown.length === 1 ? 'is' : 'are'} configured but not shown — ARGUS needs an update to
      display {unknown.length === 1 ? 'it' : 'them'}.
    </p>
  );
};
